import { useState } from 'react'
import { Select, Button, Space, Typography, Spin, message as antMessage } from 'antd'
import { RefreshCw, FolderOpen, Settings, Plus } from 'lucide-react'
import { useProjects, useReindexProject } from '@/api/projects.api'
import { useUIStore } from '@/store/uiStore'
import { ProjectSettingsModal } from '../ProjectSettingsModal'
import { t } from '@/config/translations'
import { branding } from '@/config/branding'

const { Text } = Typography

export function ProjectSelector() {
  const { selectedProjectId, setSelectedProjectId, isReindexing, setReindexing } = useUIStore()
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('edit')
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null)

  const { data: projectsData, isLoading, error } = useProjects()
  const reindexMutation = useReindexProject()

  const handleProjectChange = (value: string) => {
    setSelectedProjectId(value)
  }

  const handleReindex = () => {
    if (!selectedProjectId) {
      void antMessage.warning(t('no_projects_warning'))
      return
    }

    setReindexing(true)

    reindexMutation.mutate(selectedProjectId, {
      onSuccess: () => {
        void antMessage.success(t('reindex_started'))
        setReindexing(false)
      },
      onError: (err) => {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error'
        void antMessage.error(t('connection_error', { error: errorMsg }))
        setReindexing(false)
      },
    })
  }

  const handleEditProject = () => {
    if (!selectedProjectId) {
      void antMessage.warning(t('no_projects_warning'))
      return
    }
    setEditingProjectId(selectedProjectId)
    setModalMode('edit')
    setModalOpen(true)
  }

  const handleCreateProject = () => {
    setEditingProjectId(null)
    setModalMode('create')
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setEditingProjectId(null)
  }

  if (isLoading) {
    return (
      <div style={{ padding: '16px', textAlign: 'center' }}>
        <Spin size="small" />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '16px' }}>
        <Text type="danger">{t('connection_error', { error: 'Failed to load projects' })}</Text>
      </div>
    )
  }

  const projects = projectsData?.projects ?? []
  const isDemoMode = branding.isDemo

  return (
    <>
      <div style={{ padding: '16px' }}>
        <Space vertical style={{ width: '100%' }} size={12}>
          {/* Label */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FolderOpen size={16} />
              <Text strong>{t('project_label')}</Text>
            </div>
            {!isDemoMode && (
              <Button
                type="text"
                size="small"
                icon={<Plus size={16} />}
                onClick={handleCreateProject}
                title={t('create_project') || 'Create project'}
              />
            )}
          </div>

          {/* Project Select */}
          <Select
            value={isDemoMode ? 'demo' : selectedProjectId}
            onChange={handleProjectChange}
            style={{ width: '100%' }}
            options={projects.map((project) => ({
              value: project.id,
              label: project.name,
              disabled: !project.enabled,
            }))}
            placeholder={t('project_label')}
            disabled={isDemoMode}
          />

          {/* Action Buttons - Hidden in demo mode */}
          {!isDemoMode && (
            <Space.Compact block>
              <Button
                style={{ flex: 1 }}
                icon={<RefreshCw size={16} />}
                onClick={handleReindex}
                loading={isReindexing || reindexMutation.isPending}
                disabled={!selectedProjectId}
                title={t('reindex_help')}
              >
                {isReindexing ? t('reindexing_in_progress') : t('reindex_button')}
              </Button>
              <Button
                icon={<Settings size={16} />}
                onClick={handleEditProject}
                disabled={!selectedProjectId}
                title={t('edit_project') || 'Edit project'}
              />
            </Space.Compact>
          )}
        </Space>
      </div>

      <ProjectSettingsModal
        projectId={editingProjectId}
        open={modalOpen}
        onClose={handleCloseModal}
        mode={modalMode}
      />
    </>
  )
}
